import React, { useEffect, useState } from 'react';
import ClientSidebar from '../components/ClientSidebar';
import NotificationBell from '../components/NotificationBell';
import ChatBotTrigger from '../../components/ChatBotTrigger';

import { API_BASE as API_BASE_URL } from '../../config/runtime';

const getStoredClientUser = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed?.role || '').toLowerCase() === 'client' ? parsed : null;
  } catch {
    return null;
  }
};

const NOTIFICATION_OPTIONS = [
  {
    key: 'email_bookings',
    label: 'Email Bookings',
    description: 'Receive email updates when your booking status changes',
  },
  {
    key: 'email_promotions',
    label: 'Email Promotions',
    description: 'Receive promotional offers and spa updates by email',
  },
];

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const PasswordField = ({ label, name, value, show, onToggle, onChange }) => (
  <div>
    <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{label}</label>
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 focus:border-[#b37b65] outline-none transition text-sm sm:text-base"
        placeholder={`Enter ${label.toLowerCase()}`}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
      >
        {show ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  </div>
);

const ClientSettings = () => {
  const clientUser = getStoredClientUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('password');

  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Loading and message states
  const [saving, setSaving] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [updatingPreferenceKey, setUpdatingPreferenceKey] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [notifications, setNotifications] = useState({
    email_bookings: true,
    email_promotions: false,
  });

  useEffect(() => {
    if (!clientUser?.user_id) return;

    let isMounted = true;

    const loadNotificationPreferences = async () => {
      try {
        setLoadingPreferences(true);
        const response = await fetch(`${API_BASE_URL}/get_users.php?user_id=${clientUser.user_id}`);
        const data = await response.json();

        if (!data.success || !isMounted) return;

        setNotifications({
          email_bookings: Boolean(Number(data.user?.email_bookings ?? 1)),
          email_promotions: Boolean(Number(data.user?.email_promotions ?? 0)),
        });
      } catch (error) {
        console.error('Error loading notification preferences:', error);
      } finally {
        if (isMounted) {
          setLoadingPreferences(false);
        }
      }
    };

    loadNotificationPreferences();

    return () => {
      isMounted = false;
    };
  }, [clientUser?.user_id]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    setShowPasswordModal(true);
  };

  const confirmPasswordUpdate = async () => {
    if (!clientUser?.user_id) {
      setMessage({ type: 'error', text: 'User not logged in' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await fetch(`${API_BASE_URL}/update_password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: clientUser.user_id,
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordModal(false);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update password' });
        setShowPasswordModal(false);
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({ type: 'error', text: 'Network error. Please check your connection.' });
      setShowPasswordModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationChange = async (key) => {
    if (!clientUser?.user_id || updatingPreferenceKey) return;

    const nextValue = !notifications[key];
    const nextNotifications = { ...notifications, [key]: nextValue };

    setNotifications(nextNotifications);
    setUpdatingPreferenceKey(key);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/update_notification_preferences.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: clientUser.user_id,
          email_bookings: nextNotifications.email_bookings,
          email_promotions: nextNotifications.email_promotions,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setNotifications((prev) => ({ ...prev, [key]: !nextValue }));
        setMessage({ type: 'error', text: data.message || 'Failed to update notification preferences' });
        return;
      }

      setNotifications({
        email_bookings: Boolean(data.preferences?.email_bookings),
        email_promotions: Boolean(data.preferences?.email_promotions),
      });
      setMessage({ type: 'success', text: data.message || 'Notification preferences updated successfully' });
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      setNotifications((prev) => ({ ...prev, [key]: !nextValue }));
      setMessage({ type: 'error', text: 'Network error. Please check your connection.' });
    } finally {
      setUpdatingPreferenceKey('');
    }
  };

  const handleDeleteAccount = () => {
    setMessage({ type: '', text: '' });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!clientUser?.user_id) {
      setMessage({ type: 'error', text: 'User not logged in' });
      return;
    }

    try {
      setDeletingAccount(true);
      setMessage({ type: '', text: '' });

      const response = await fetch(`${API_BASE_URL}/delete_user.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: clientUser.user_id,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setMessage({ type: 'error', text: data.message || 'Failed to delete account' });
        return;
      }

      setShowDeleteModal(false);
      setDeleteConfirmText('');
      localStorage.removeItem('user');
      alert(data.message || 'Account deleted successfully!');
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessage({ type: 'error', text: 'Network error. Please check your connection.' });
    } finally {
      setDeletingAccount(false);
    }
  };

  const tabs = [
    { id: 'password', label: 'Change Password' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'account', label: 'Account' },
  ];

  return (
    <div className="min-h-screen hygea-app-bg">
      {/* Notification Bell */}
      <div className="fixed top-4 right-4 sm:right-6 z-50">
        <NotificationBell isScrolled={false} forceBrown={true} />
      </div>

      <ClientSidebar
        isOpen={isSidebarOpen}
        onOpenChange={setIsSidebarOpen}
        clientName={clientUser?.full_name}
        clientEmail={clientUser?.email}
        clientAvatar={clientUser?.avatar_url}
        onLogout={() => { window.location.href = '/'; }}
      />

      <div className="transition-all duration-300">
        {/* Responsive padding: tighter on mobile */}
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto">

            {/* Header */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Settings</h2>
              <p className="text-gray-500 mt-1 text-sm sm:text-base">Manage your account settings and preferences</p>
            </div>

            {/* Layout: stacked on mobile, side-by-side on md+ */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-6">

              {/* Tab Nav — horizontal scroll on mobile, vertical sidebar on md+ */}
              <div className="md:w-56 lg:w-64 flex-shrink-0">
                <div className="bg-white rounded-2xl p-2 sm:p-4 shadow-sm border border-gray-200">
                  {/* Mobile: horizontal scrollable tabs */}
                  <nav className="flex md:flex-col gap-1">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                          flex-1 md:flex-none text-center md:text-left px-2 sm:px-4 py-2.5 sm:py-3
                          rounded-xl font-bold text-xs sm:text-sm transition w-full
                          ${activeTab === tab.id
                            ? 'bg-[#b37b65] text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                          }
                        `}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Content Panel */}
              <div className="flex-1 min-w-0">

                {/* ── Change Password ── */}
                {activeTab === 'password' && (
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-200">
                    {message.text && (
                      <div className={`mb-5 p-3 sm:p-4 rounded-xl text-sm ${
                        message.type === 'success'
                          ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                          : 'bg-rose-50 border border-rose-200 text-rose-700'
                      }`}>
                        <p className="font-medium">{message.text}</p>
                      </div>
                    )}

                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-5 sm:mb-6">Change Password</h3>
                    <form onSubmit={handlePasswordSubmit} className="space-y-4">
                      <PasswordField
                        label="Current Password"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        show={showCurrentPassword}
                        onToggle={() => setShowCurrentPassword(!showCurrentPassword)}
                      />
                      <PasswordField
                        label="New Password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        show={showNewPassword}
                        onToggle={() => setShowNewPassword(!showNewPassword)}
                      />
                      <PasswordField
                        label="Confirm New Password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        show={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                      />
                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-[#b37b65] text-white rounded-xl font-bold text-sm sm:text-base hover:bg-[#996758] transition active:scale-[0.98]"
                      >
                        Update Password
                      </button>
                    </form>
                  </div>
                )}

                {/* ── Notifications ── */}
                {activeTab === 'notifications' && (
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-200">
                    {message.text && (
                      <div className={`mb-5 p-3 sm:p-4 rounded-xl text-sm ${
                        message.type === 'success'
                          ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                          : 'bg-rose-50 border border-rose-200 text-rose-700'
                      }`}>
                        <p className="font-medium">{message.text}</p>
                      </div>
                    )}

                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-5 sm:mb-6">Notification Preferences</h3>
                    <div className="space-y-1">
                      {NOTIFICATION_OPTIONS.map(({ key, label, description }) => {
                        const value = notifications[key];

                        return (
                        <div
                          key={key}
                          className="flex justify-between items-center py-3 sm:py-4 border-b border-gray-100 last:border-0 gap-4"
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-gray-800 text-sm sm:text-base truncate">
                              {label}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500">
                              {loadingPreferences ? 'Loading preference...' : description}
                            </p>
                          </div>
                          {/* Toggle — larger tap target on mobile */}
                          <button
                            type="button"
                            onClick={() => handleNotificationChange(key)}
                            disabled={loadingPreferences || !!updatingPreferenceKey}
                            role="switch"
                            aria-checked={value}
                            className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#b37b65] focus:ring-offset-2 ${
                              value ? 'bg-[#b37b65]' : 'bg-gray-300'
                            } disabled:opacity-60 disabled:cursor-not-allowed`}
                            aria-label={label}
                          >
                            <span
                              className={`absolute left-1 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow transform transition-transform duration-300 ${
                                value ? 'translate-x-7' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                        );
                      })}
                    </div>
                    {updatingPreferenceKey && (
                      <p className="mt-4 text-xs sm:text-sm text-gray-500">
                        {`Saving ${updatingPreferenceKey === 'email_bookings' ? 'email bookings' : 'email promotions'} preference...`}
                      </p>
                    )}
                  </div>
                )}

                {/* ── Account ── */}
                {activeTab === 'account' && (
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-200">
                    {message.text && (
                      <div className={`mb-5 p-3 sm:p-4 rounded-xl text-sm ${
                        message.type === 'success'
                          ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                          : 'bg-rose-50 border border-rose-200 text-rose-700'
                      }`}>
                        <p className="font-medium">{message.text}</p>
                      </div>
                    )}

                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-5 sm:mb-6">Account Settings</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl">
                        <p className="font-medium text-gray-800 text-sm sm:text-base">Delete Account</p>
                        <p className="text-xs sm:text-sm text-gray-500 mb-3">Permanently delete your account and all data</p>
                        <button
                          onClick={handleDeleteAccount}
                          className="w-full sm:w-auto px-4 py-2.5 bg-rose-500 text-white rounded-xl font-bold text-sm hover:bg-rose-600 transition active:scale-[0.98]"
                        >
                          Delete Account
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Password Confirmation Modal ── */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-md">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">Confirm Password Update</h3>
            <p className="text-gray-600 text-sm sm:text-base mb-6">
              Are you sure you want to update your password? You will be required to log in again on all devices.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmPasswordUpdate}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-[#b37b65] text-white rounded-xl font-bold text-sm hover:bg-[#996758] transition disabled:opacity-60"
              >
                {saving ? 'Updating…' : 'Confirm Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Account Modal ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl border-t-4 sm:border-2 border-rose-500 p-5 sm:p-6 w-full sm:max-w-md">
            <h3 className="text-lg sm:text-xl font-bold text-rose-600 mb-3">Permanently delete account?</h3>
            <p className="text-gray-600 text-sm sm:text-base mb-4">
              This action cannot be undone and all your data will be removed.
            </p>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <span className="font-bold text-rose-600">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-rose-500 outline-none transition text-sm sm:text-base"
                placeholder="Type DELETE here"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                disabled={deletingAccount}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                className="flex-1 px-4 py-3 bg-rose-500 text-white rounded-xl font-bold text-sm hover:bg-rose-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingAccount ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ChatBotTrigger />
    </div>
  );
};

export default ClientSettings;
